/**
 * PwaInstallNudge — Day-3+ install snackbar (iter196 W4 mobile polish).
 *
 * Rationale (from the UX assessment): the canonical PWA install prompt
 * timing in retention research is Day 3, NOT Day 1. Showing it on first
 * load adds friction during the most fragile activation window; Day 3
 * targets users who already have signal that they value the app.
 *
 * Visibility rules (all must be true):
 *   • User authed AND `user.created_at` >= 3 days ago
 *   • Not already running standalone (PWA installed)
 *   • `beforeinstallprompt` was captured by `usePwaInstall` (i.e. the
 *     browser supports it AND the user is on Chrome/Edge Android)
 *   • Not previously dismissed (localStorage key)
 *   • Not on a public/auth route (/, /login, /signup, etc.)
 *
 * Surface: bottom-aligned snackbar that respects the mobile bottom-nav
 * height. Auto-dismisses after a successful install ('appinstalled'
 * event) and persists the dismissal for 30 days so we don't nag.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import usePwaInstall from '../hooks/usePwaInstall';

const DISMISS_KEY = 'isp_pwa_install_dismissed_until';
const MIN_DAYS_SINCE_SIGNUP = 3;
const SUPPRESS_AFTER_DISMISS_DAYS = 30;

const PUBLIC_ROUTES = ['/', '/login', '/signup', '/auth', '/founder', '/claim-pioneer', '/mobile', '/pricing'];

const daysSince = (iso) => {
  if (!iso) return 0;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 0;
    return Math.max(0, Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000)));
  } catch { return 0; }
};

const isDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch { return false; }
};

const markDismissed = () => {
  try {
    const until = Date.now() + SUPPRESS_AFTER_DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch { /* ignore */ }
};

const PwaInstallNudge = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { canInstall, isStandalone, isInstalled, promptInstall } = usePwaInstall();
  const [hidden, setHidden] = useState(isDismissed);

  // If the user installs from somewhere else, retire the nudge.
  useEffect(() => {
    if (isInstalled || isStandalone) setHidden(true);
  }, [isInstalled, isStandalone]);

  if (hidden) return null;
  if (!user) return null;
  if (daysSince(user.created_at) < MIN_DAYS_SINCE_SIGNUP) return null;
  if (PUBLIC_ROUTES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) return null;
  if (!canInstall) return null;

  const handleInstall = async () => {
    const result = await promptInstall();
    if (result?.outcome === 'accepted' || result?.outcome === 'dismissed') {
      markDismissed();
      setHidden(true);
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setHidden(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ type: 'spring', stiffness: 220, damping: 24 }}
        // Sits ABOVE the mobile bottom-nav (uses the same height token) so
        // the snackbar never hides actionable nav items.
        className="fixed left-4 right-4 z-[60] mx-auto max-w-md lg:hidden"
        style={{ bottom: 'calc(var(--isp-bottom-nav-height, 88px) + 0.75rem)' }}
        data-testid="pwa-install-nudge"
        role="dialog"
        aria-labelledby="pwa-install-title"
      >
        <div className="rounded-2xl bg-white/95 backdrop-blur-md shadow-2xl border border-[var(--isp-glass-border)] p-3.5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#4F2BB8] flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-white" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p id="pwa-install-title" className="text-sm font-semibold text-[var(--isp-heading)] leading-tight">
              Install iStudyPRO
            </p>
            <p className="text-[11px] text-[var(--isp-body)] mt-0.5 leading-snug">
              One-tap launch · works offline · zero notifications spam.
            </p>
          </div>
          <button
            type="button"
            onClick={handleInstall}
            className="px-3 py-2 rounded-lg bg-[#002FA7] text-white text-xs font-semibold whitespace-nowrap hover:bg-[#00227A] transition-colors min-h-[44px]"
            data-testid="pwa-install-accept"
          >
            Install
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-8 h-8 rounded-full text-[var(--isp-muted)] hover:bg-slate-100 flex items-center justify-center"
            aria-label="Dismiss install prompt"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PwaInstallNudge;
